import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  clientCreateBaaAccount,
  clientCreateBaaPerson,
  clientRemoveAssignedAccount,
  clientRevokeUserAccess,
  useGraphqlClient,
} from '../graphql';
import {
  CreateBaaAccountInput,
  CreateBaaPersonInput,
  MutationRevokeUserAccessArgs,
  RemoveAssignedAccountInput,
  useAssignedBaaAccountsForFirmMemberQuery,
  useAssignedBaaAccountsForFirmQuery,
  useUnassignedBaaAccountsForFirmMemberQuery,
  useUnassignedBaaAccountsForFirmQuery,
} from '../generated/graphql';
import { baaKeys } from './queryKeys';
import { useState } from 'react';

export const useRevokeBaaUserAccess = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const [firmID, setFirmID] = useState<string>();

  const revokeBaaUserAccess = useMutation({
    mutationFn: (input: MutationRevokeUserAccessArgs) => {
      setFirmID(input.firmID);
      return clientRevokeUserAccess(gqlClient, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(baaKeys.baaPersonForSelf(firmID));
    },
  });

  return { revokeBaaUserAccess };
};

export const useCreateBaaAccount = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();

  const createBaaAccount = useMutation({
    mutationKey: ['createBaaAccount'],
    mutationFn: async (input: CreateBaaAccountInput) => {
      return clientCreateBaaAccount(gqlClient, input);
    },
    onSuccess: (_, { firmID, firmMemberID }) => {
      queryClient.invalidateQueries(useAssignedBaaAccountsForFirmMemberQuery.getKey({ firmID, firmMemberID }));
      queryClient.invalidateQueries(useUnassignedBaaAccountsForFirmMemberQuery.getKey({ firmID, firmMemberID }));
      queryClient.invalidateQueries(useUnassignedBaaAccountsForFirmQuery.getKey({ firmID }));
      queryClient.invalidateQueries(useAssignedBaaAccountsForFirmQuery.getKey({ firmID }));
    },
  });

  return { createBaaAccount };
};

export const useRemoveAssignedAccount = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();

  const removeAssignedAccount = useMutation({
    mutationKey: ['removeAssignedAccount'],
    mutationFn: async (input: RemoveAssignedAccountInput) => {
      return clientRemoveAssignedAccount(gqlClient, input);
    },
    onSuccess: async (_, { firmID, firmMemberID }) => {
      queryClient.invalidateQueries(useUnassignedBaaAccountsForFirmQuery.getKey({ firmID }));
      queryClient.invalidateQueries(useAssignedBaaAccountsForFirmQuery.getKey({ firmID }));
      if (firmMemberID) {
        queryClient.invalidateQueries(useAssignedBaaAccountsForFirmMemberQuery.getKey({ firmID, firmMemberID }));
        queryClient.invalidateQueries(useUnassignedBaaAccountsForFirmMemberQuery.getKey({ firmID, firmMemberID }));
      }
    },
  });

  return { removeAssignedAccount };
};

export const useCreateBaaPerson = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const [firmID, setFirmID] = useState<string>();

  const createBaaPerson = useMutation({
    mutationFn: (input: CreateBaaPersonInput) => {
      setFirmID(input.firmID);
      return clientCreateBaaPerson(gqlClient, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(baaKeys.baaPersonForSelf(firmID));
    },
  });

  return { createBaaPerson };
};
